import { computed, ref, type Ref } from "vue";
import { useUserProfileStore } from "@stores/userProfileStore";

// NOTE: Constant is defined server-side also (DON'T change without reviewing that code)
export const SOCIAL_CALENDAR_MODE_LIVE = "default" as const;
// Currently we just have 1 test mode but the server should suppport as many test calendars as we want
export const SOCIAL_CALENDAR_MODE_TEST = "test" as const;

export type SocialCalendarMode = typeof SOCIAL_CALENDAR_MODE_TEST | typeof SOCIAL_CALENDAR_MODE_LIVE;

// In-memory singleton state
const modeRef = ref<SocialCalendarMode | null>(null);
const STORAGE_KEY = "shrpa.calendar.testMode";

function ensureInitialized() {
  if (modeRef.value !== null) {
    return;
  }

  try {
    // Defaults to test mode for superusers
    const userProfileStore = useUserProfileStore();
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    const defaultMode: SocialCalendarMode = userProfileStore.isSuperOrSalesUser ? SOCIAL_CALENDAR_MODE_TEST : SOCIAL_CALENDAR_MODE_LIVE;
    const initial: SocialCalendarMode = stored === SOCIAL_CALENDAR_MODE_TEST || stored === SOCIAL_CALENDAR_MODE_LIVE ? (stored as SocialCalendarMode) : defaultMode;
    modeRef.value = initial;

    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, initial);
    }
  } catch {
    console.error("Failed to set social calendar mode");
    // Default to live mode so customers can't get stuck in test mode
    modeRef.value = SOCIAL_CALENDAR_MODE_LIVE;
  }
}

export function getCalendarTestMode(): SocialCalendarMode {
  ensureInitialized();
  return modeRef.value as SocialCalendarMode;
}

export function setCalendarTestMode(mode: SocialCalendarMode): void {
  ensureInitialized();
  modeRef.value = mode;
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  } catch {
    console.error("Failed to set social calendar mode");
  }
}

export function toggleCalendarTestMode(): void {
  ensureInitialized();
  const next = modeRef.value === SOCIAL_CALENDAR_MODE_TEST ? SOCIAL_CALENDAR_MODE_LIVE : SOCIAL_CALENDAR_MODE_TEST;
  modeRef.value = next;
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    console.error("Failed to toggle social calendar mode");
  }
}

export function useCalendarTestModeManager(): {
  mode: Ref<SocialCalendarMode>;
  isTest: Ref<boolean>;
  setMode: (mode: SocialCalendarMode) => void;
  toggle: () => void;
} {
  ensureInitialized();
  const isTest = computed(() => modeRef.value === SOCIAL_CALENDAR_MODE_TEST);
  return {
    mode: modeRef as Ref<SocialCalendarMode>,
    isTest,
    setMode: setCalendarTestMode,
    toggle: toggleCalendarTestMode,
  };
}

export default {
  SOCIAL_CALENDAR_MODE_TEST,
  SOCIAL_CALENDAR_MODE_LIVE,
  getCalendarTestMode,
  setCalendarTestMode,
  toggleCalendarTestMode,
  useCalendarTestModeManager,
};
